import { HTTPError } from 'ky';

export type { NonLiveJurisdictionAction, QuotePostRequestBodyWithNonLiveJurisdictionAction } from '@models/newQuote';

// Thrown when an internal user dismisses the jurisdiction-not-live dialog without picking either
// option, so the caller can distinguish a deliberate cancellation from a real creation failure.
export class NonLiveJurisdictionDialogCancelledError extends Error {}

// The backend contract for this specific rejection is still being finalized in a companion
// backend PR (LIO-28733). Today, without the `nonLiveJurisdictionAction` field, quote creation
// hard-rejects a jurisdiction that isn't live/approved for the brokerage's offering with a 422
// (DataNotProcessable) whose message is exactly this string (see insertQuote /
// isBrokerageOfferingValid in lio-portal-api's src/api/quote/quote.ts). There is no dedicated
// error code distinguishing this from other 422s today, so this is a best-effort match on the
// current message text and will need to be revisited once the API contract is finalized.
const NON_LIVE_JURISDICTION_ERROR_MESSAGE = 'The brokerage offering validation failed.';

export const isNonLiveJurisdictionError = async (error: unknown): Promise<boolean> => {
  if (!(error instanceof HTTPError) || error.response.status !== 422) {
    return false;
  }
  try {
    const body = (await error.response.clone().json()) as { error?: { message?: string } };
    return body?.error?.message?.includes(NON_LIVE_JURISDICTION_ERROR_MESSAGE) ?? false;
  } catch {
    return false;
  }
};

export const NON_LIVE_JURISDICTION_DIALOG_COPY = {
  title: 'Jurisdiction Not Live for This Offering',
  description:
    'This brokerage is not currently approved to write this offering in the selected jurisdiction. Choose how to proceed:',
  declineOptionLabel: 'Create and Decline',
  declineOptionDescription:
    'Captures this as a lead and immediately sends a decline to the broker, so the submission is not silently dropped.',
  internalOnlyOptionLabel: 'Create Only (Internal)',
  internalOnlyOptionDescription:
    'Creates the quote but keeps it internal-only, hidden from the broker and producer. It is a dead end until an authorized underwriter applies the LSIC issuing-company override to make the jurisdiction viable — this quote cannot otherwise be used or made valid.',
  cancelLabel: 'Cancel',
};
